import type { CheckoutPath } from '@/lib/checkout/types'
import { trackCheckoutLoadStep } from '@/lib/gtm/gtmV2'
import type { CheckoutLoadStepEventName } from '@/lib/gtm/typesV2'

/**
 * Times the path from a PDP subscribe click to a usable checkout form, sending one GTM
 * event per step as it happens, each stamped with `epoch_ms_occurred_at` (milliseconds since the
 * Unix epoch; a number, because Mixpanel stores ISO date strings to the second only) so the
 * time between steps can be measured downstream. Every event in one journey shares a random
 * `checkout_attempt_id`, so a journey can be pieced together without relying on a cookie,
 * and the usual checkout `user_id` (hashed email once a step knows it, else the quiz cookie).
 *
 * Shoppers who aren't logged in pass through signup/login first; `auth_completed` marks the
 * moment they leave it for checkout, so their load can be timed from there too.
 */

export type CheckoutLoadStep =
  | 'subscribe_click'
  | 'auth_completed'
  | 'checkout_mounted'
  | 'user_established'
  | 'eligibility_checked'
  | 'session_created'
  | 'stripe_loaded'
  | 'elements_ready'

const EVENT_NAMES: Record<CheckoutLoadStep, CheckoutLoadStepEventName> = {
  subscribe_click: 'checkout_subscribe_clicked',
  auth_completed: 'checkout_auth_completed',
  checkout_mounted: 'checkout_page_mounted',
  user_established: 'checkout_user_established',
  eligibility_checked: 'checkout_eligibility_checked',
  session_created: 'checkout_session_created',
  stripe_loaded: 'checkout_stripe_loaded',
  elements_ready: 'checkout_form_ready',
}

type CheckoutJourney = {
  attemptId: string
  email?: string | null
  checkoutPath?: CheckoutPath
  sentSteps: Set<CheckoutLoadStep>
}

// One journey at a time, held in the tab's memory so it survives client-side navigation.
let currentJourney: CheckoutJourney | null = null

const startJourney = (): CheckoutJourney => {
  currentJourney = { attemptId: crypto.randomUUID(), sentSteps: new Set() }
  return currentJourney
}

const sendStep = (journey: CheckoutJourney, step: CheckoutLoadStep, email?: string | null) => {
  if (journey.sentSteps.has(step)) return
  journey.sentSteps.add(step)
  if (email) journey.email = email

  trackCheckoutLoadStep({
    eventName: EVENT_NAMES[step],
    attemptId: journey.attemptId,
    occurredAtEpochMs: Date.now(),
    email: journey.email,
    checkoutPath: journey.checkoutPath,
  })
}

/** Call on the PDP subscribe click; starts a new journey. */
export const startCheckoutLoadTiming = (email?: string | null) => {
  if (typeof window === 'undefined') return
  sendStep(startJourney(), 'subscribe_click', email)
}

/** Sends a step's event the first time it happens in this journey; retries and re-renders are ignored. */
export const markCheckoutLoadStep = (step: CheckoutLoadStep, email?: string | null) => {
  if (typeof window === 'undefined') return
  // Signup/login reached without a click (e.g. a direct link) still starts a journey.
  const journey = currentJourney ?? (step === 'auth_completed' ? startJourney() : null)
  if (journey) sendStep(journey, step, email)
}

/** Call when the checkout page mounts. A second visit without a new click starts a fresh journey. */
export const startCheckoutPageTiming = () => {
  if (typeof window === 'undefined') return
  if (!currentJourney || currentJourney.sentSteps.has('checkout_mounted')) startJourney()
  markCheckoutLoadStep('checkout_mounted')
}

/** Call once the page has chosen its form, so the steps after it can be split by form. */
export const setCheckoutLoadPath = (checkoutPath: CheckoutPath) => {
  if (currentJourney) currentJourney.checkoutPath = checkoutPath
}

/** Call once the form is usable: sends the last step and ends the journey. */
export const completeCheckoutLoadTiming = () => {
  if (typeof window === 'undefined' || !currentJourney) return
  markCheckoutLoadStep('elements_ready')
  currentJourney = null
}
